import { produce } from 'immer';
import { findIndex, find, reject, isNil, last, map } from 'lodash/fp';
import { nanoid } from 'nanoid';
import { JoinType } from 'app/lib/constants';
import { extractError } from 'app/lib/error';
import { reorderArray } from 'app/lib/utils';
import {
    ADD_SOURCE,
    CHANGE_COLUMN_SELECTION,
    CHANGE_CRITERIA,
    CHANGE_JOIN_TYPE,
    CREATE,
    INITIALIZE,
    INITIALIZE_CONFIG,
    PREVIEW,
    REMOVE_CRITERIA,
    REMOVE_SOURCE,
    RENAME_COLUMN,
    SELECT_KEY,
    SELECT_SOURCE,
    UPDATE,
    REORDER_SOURCES,
    REMOVE_COLUMNS,
    CHANGE_TAB
} from './types.js';

const SOURCE_INITIAL_STATE = {
    id: null,
    uid: null,
    name: null,
    selectedKey: null,
    joinType: JoinType.INNER,
    excludes: [],
    renames: {}
};

const CRITERIA_INITIAL_STATE = {
    id: null,
    left: {
        id: null,
        column: null
    },
    right: {
        id: null,
        column: null
    }
};

export const INITIAL_STATE = {
    config: {
        sources: [],
        criteria: [],
        firstKey: null,
        selectedSourceId: null
    },
    error: null,
    currentTab: null,
    isSaving: false
};

export const multiJoinReducer = produce((draft, action) => {
    switch (action.type) {
        case INITIALIZE:
            return { ...INITIAL_STATE };

        case INITIALIZE_CONFIG:
            draft.config.sources = action.payload.sources;
            draft.config.criteria = action.payload.criteria;
            break;

        case ADD_SOURCE: {
            const { id, uid, name, renames } = action.payload;

            draft.config.sources.push({
                ...SOURCE_INITIAL_STATE,
                id,
                uid,
                name,
                renames
            });
            draft.config.selectedSourceId = id;

            break;
        }

        case REMOVE_SOURCE: {
            const source = draft.config.sources[action.payload.index];

            draft.config.sources.splice(action.payload.index, 1);
            draft.config.criteria = reject(
                ({ left, right }) => left?.id === source.id || right?.id === source.id,
                draft.config.criteria
            );

            if (draft.config.selectedSourceId === source.id) {
                draft.config.selectedSourceId = last(draft.config.sources)?.id ?? null;
            }

            if (draft.config.firstKey?.id === source.id) {
                draft.config.firstKey = null;
            }

            break;
        }

        case REORDER_SOURCES: {
            const { oldIndex, newIndex } = action.payload;

            const reorderedArray = reorderArray(draft.config.sources, oldIndex, newIndex);
            draft.config.sources = reorderedArray;

            draft.config.criteria = map((criterion) => {
                const { left, right } = criterion;
                const leftIndex = findIndex({ id: left.id }, reorderedArray);
                const rightIndex = findIndex({ id: right.id }, reorderedArray);
                return leftIndex > rightIndex
                    ? { ...criterion, left: right, right: left }
                    : criterion;
            }, draft.config.criteria);

            break;
        }

        case SELECT_SOURCE:
            draft.config.selectedSourceId = action.payload.id;
            break;

        case SELECT_KEY: {
            const { id, column } = action.payload;
            const sourceIndex = findIndex({ id }, draft.config.sources);

            if (isNil(draft.config.firstKey) || draft.config.firstKey.id === id) {
                draft.config.firstKey = { id, column };
                draft.config.sources[sourceIndex].selectedKey = column;
            } else {
                const otherSourceIndex = findIndex(
                    { id: draft.config.firstKey.id },
                    draft.config.sources
                );

                const key = sourceIndex < otherSourceIndex ? 'left' : 'right';
                const otherKey = sourceIndex < otherSourceIndex ? 'right' : 'left';
                const rightColumn = key === 'right' ? column : draft.config.firstKey.column;
                const righSourceIndex = key === 'right' ? sourceIndex : otherSourceIndex;

                draft.config.criteria.push({
                    ...CRITERIA_INITIAL_STATE,
                    id: nanoid(),
                    [key]: { id, column },
                    [otherKey]: draft.config.firstKey
                });
                draft.config.firstKey = null;
                draft.config.sources[sourceIndex].selectedKey = null;
                draft.config.sources[otherSourceIndex].selectedKey = null;

                if (!draft.config.sources[righSourceIndex].excludes.includes(rightColumn)) {
                    draft.config.sources[righSourceIndex].excludes.push(rightColumn);
                }
            }

            break;
        }

        case CHANGE_CRITERIA: {
            const { id, left, right } = action.payload;
            const criteria = find({ id }, draft.config.criteria);
            const previousRightKey = criteria.right.column;

            if (left) {
                criteria.left.column = left;
            }

            if (right) {
                criteria.right.column = right;
            }

            if (right && previousRightKey !== right) {
                const sourceIndex = findIndex({ id: criteria.right.id }, draft.config.sources);
                const excludes = draft.config.sources[sourceIndex].excludes;
                draft.config.sources[sourceIndex].excludes = excludes.filter(
                    (exclude) => exclude !== previousRightKey
                );
                if (!excludes.includes(right)) {
                    draft.config.sources[sourceIndex].excludes.push(right);
                }
            }
            break;
        }

        case CHANGE_JOIN_TYPE: {
            const { id, joinType } = action.payload;
            const source = find({ id }, draft.config.sources);

            source.joinType = joinType;

            break;
        }

        case REMOVE_CRITERIA: {
            const { id } = action.payload;
            const index = findIndex({ id }, draft.config.criteria);
            const criteria = draft.config.criteria[index];

            draft.config.criteria.splice(index, 1);

            const sourceIndex = findIndex({ id: criteria.right.id }, draft.config.sources);
            const excludes = draft.config.sources[sourceIndex].excludes;

            draft.config.sources[sourceIndex].excludes = excludes.filter(
                (exclude) => exclude !== criteria.right.column
            );

            break;
        }

        case CHANGE_COLUMN_SELECTION: {
            const { id, excludes, renames } = action.payload;
            const source = find({ id }, draft.config.sources);

            source.excludes = excludes;
            source.renames = renames;

            break;
        }

        case RENAME_COLUMN: {
            const { id, name, alias } = action.payload;
            const source = find({ id }, draft.config.sources);

            source.renames ||= {};
            source.renames[name] = alias;

            break;
        }

        case REMOVE_COLUMNS: {
            const { errors } = action.payload;

            const getSource = (id) => find({ id }, draft.config.sources);

            errors.forEach((error) => {
                const { viewUid, columnNames } = error;

                draft.config.sources.forEach((source) => {
                    if (source.uid === viewUid) {
                        source.excludes = source.excludes?.filter(
                            (name) => !columnNames.includes(name)
                        );

                        if (source.renames) {
                            columnNames.forEach((name) => {
                                if (source.renames.hasOwnProperty(name)) {
                                    delete source.renames[name];
                                }
                            });
                        }
                    }
                });

                draft.config.criteria = draft.config.criteria.filter((criterion) => {
                    const leftSource = getSource(criterion.left.id);
                    const rightSource = getSource(criterion.right.id);

                    return !(
                        (leftSource.uid === viewUid &&
                            columnNames.includes(criterion.left.column)) ||
                        (rightSource.uid === viewUid &&
                            columnNames.includes(criterion.right.column))
                    );
                });
            });

            break;
        }

        case PREVIEW:
            draft.error = null;
            break;

        case CREATE.STARTED:
        case UPDATE.STARTED:
            draft.isSaving = true;
            break;
        case CREATE.ENDED:
        case UPDATE.ENDED:
            draft.isSaving = false;
            break;

        case CREATE.REJECTED:
        case CREATE.FAILED:
        case UPDATE.REJECTED:
        case UPDATE.FAILED:
            draft.error = extractError(action.payload);
            break;

        case CHANGE_TAB:
            draft.currentTab = action.payload.tab;
            break;

        default:
            break;
    }
}, INITIAL_STATE);
